export const faqSections = [
  {
    "id": 1,
    "title": "About Viriqo Hires",
    "description": "Core questions for buyers first learning what Viriqo Hires is and where it fits in the technical hiring process.",
    "questions": [
      {
        "id": "1-1",
        "question": "What is Viriqo Hires?",
        "answer": "Viriqo Hires is an AI-powered technical interview platform that brings technical interviewing, coding assessment, candidate monitoring, structured evaluation, recordings, and reporting into one connected workflow."
      },
      {
        "id": "1-2",
        "question": "What part of the hiring process does Viriqo Hires support?",
        "answer": "Viriqo Hires supports the technical interview workflow from creating a role and defining the skills to evaluate, through interview scheduling, AI-powered interviewing, coding assessment, interview monitoring, candidate evaluation, and post-interview review."
      },
      {
        "id": "1-3",
        "question": "Is Viriqo Hires only an AI interview tool?",
        "answer": "No. AI-powered interviewing is one part of Viriqo Hires. The platform also supports technical and coding assessments, interview monitoring and proctoring, structured scoring, candidate notes, recordings, transcripts, and reports."
      },
      {
        "id": "1-4",
        "question": "Who is Viriqo Hires designed for?",
        "answer": "Viriqo Hires is designed for organizations that hire technical talent. Typical users include talent acquisition teams, technical recruiters, recruitment operations teams, hiring managers, engineering leaders, and other stakeholders who review technical candidates."
      },
      {
        "id": "1-5",
        "question": "What types of roles can Viriqo Hires be used to evaluate?",
        "answer": "Viriqo Hires can be configured around the skills and assessment requirements of different technical roles. Teams can define role-specific skills, questions, evaluation weightings, interview structure, and supported coding languages according to the job being evaluated."
      },
      {
        "id": "1-6",
        "question": "Can Viriqo Hires support both technical screening and technical interviewing?",
        "answer": "Yes. Viriqo Hires' configurable skills, questions, coding assessments, AI-powered interviews, and structured evaluation can support technical screening as well as more detailed technical interview workflows."
      }
    ]
  },
  {
    "id": 2,
    "title": "Platform & Hiring Workflow",
    "description": "How Viriqo Hires connects the technical interview journey from requisition setup through review.",
    "questions": [
      {
        "id": "2-1",
        "question": "How does a typical Viriqo Hires interview workflow work?",
        "answer": "A team creates the job or requisition, defines the skills and evaluation weightings, configures the interview, schedules the candidate, conducts the AI-powered technical interview and assessment, monitors interview activity when required, and reviews the resulting score, report, recordings, transcript, notes, and other available interview evidence."
      },
      {
        "id": "2-2",
        "question": "Can recruiters create jobs directly in Viriqo Hires?",
        "answer": "Yes. Viriqo Hires includes a job creation workflow where recruiters can provide information such as the requisition ID, job title, job posting URL, job description, interview date and time, evaluation weighting, and coding language requirements."
      },
      {
        "id": "2-3",
        "question": "Can Viriqo Hires generate skills from a job description?",
        "answer": "Yes. Viriqo Hires supports generating relevant technical skills from the supplied job description. These generated skills can then be used as part of the role's interview and evaluation setup."
      },
      {
        "id": "2-4",
        "question": "Can hiring teams control how different evaluation components are weighted?",
        "answer": "Yes. Viriqo Hires allows evaluation weighting to be configured for the role. This helps teams define how strongly different evaluation components contribute to the overall assessment."
      },
      {
        "id": "2-5",
        "question": "Can teams configure interviews differently for different jobs?",
        "answer": "Yes. Jobs can be configured according to their individual technical requirements, including skills, questions, evaluation weighting, interview setup, and coding languages."
      },
      {
        "id": "2-6",
        "question": "Can recruiters track active and scheduled interviews?",
        "answer": "Yes. The interview workspace separates interviews into ongoing, pending today, upcoming, and completed states so recruiters and managers can understand where interviews are in the workflow."
      }
    ]
  },
  {
    "id": 3,
    "title": "AI-Powered Technical Interviews",
    "description": "Questions about how Viriqo Hires' AI interviewer supports structured technical interviewing.",
    "questions": [
      {
        "id": "3-1",
        "question": "How does Viriqo Hires use AI during technical interviews?",
        "answer": "Viriqo Hires uses an AI interviewer to conduct technical interviews based on the configured role, skills, questions, and interview requirements. The AI interviewer can guide the conversation and collect structured information for later evaluation."
      },
      {
        "id": "3-2",
        "question": "Can the AI interviewer ask follow-up questions?",
        "answer": "Yes. Viriqo Hires supports adaptive follow-up questions during the interview, allowing the conversation to respond to the candidate's answers while remaining connected to the configured technical evaluation."
      },
      {
        "id": "3-3",
        "question": "Are all candidates required to receive exactly the same questions?",
        "answer": "Viriqo Hires can structure interviews around defined role requirements and evaluation criteria while still allowing adaptive follow-up questions. This helps teams maintain a consistent evaluation framework without requiring every interview interaction to be identical."
      },
      {
        "id": "3-4",
        "question": "Can interview questions be customized?",
        "answer": "Yes. Viriqo Hires supports configurable interview questions so teams can align the interview with the technical requirements of the role."
      },
      {
        "id": "3-5",
        "question": "Can questions be generated from a candidate's CV or resume?",
        "answer": "Yes. Viriqo Hires' documented capabilities include CV-based questions, allowing information from the candidate's resume to contribute to the interview context."
      },
      {
        "id": "3-6",
        "question": "Can recruiters define which skills should be evaluated?",
        "answer": "Yes. Teams can define the technical skills that are relevant to a job and use those skills as part of the interview and evaluation configuration."
      },
      {
        "id": "3-7",
        "question": "Does Viriqo Hires replace every human interview?",
        "answer": "Viriqo Hires is designed to automate and structure technical interviewing, assessment, monitoring, and evaluation. Hiring teams can use the resulting scores, reports, recordings, notes, and other interview evidence as inputs within their broader hiring process rather than treating the platform as the sole hiring decision-maker."
      }
    ]
  },
  {
    "id": 4,
    "title": "Technical & Coding Assessments",
    "description": "Questions about coding exercises and technical skill evaluation.",
    "questions": [
      {
        "id": "4-1",
        "question": "Does Viriqo Hires include coding assessments?",
        "answer": "Yes. Viriqo Hires supports coding assessment as part of the technical interview workflow, allowing technical evaluation to happen within the same broader interview process."
      },
      {
        "id": "4-2",
        "question": "Which programming languages can be selected for coding assessments?",
        "answer": "The current Viriqo Hires job configuration includes Java, Python, JavaScript, C#, C++, SQL, Go, and Ruby as selectable coding languages."
      },
      {
        "id": "4-3",
        "question": "Can more than one programming language be selected for a role?",
        "answer": "Yes. The job configuration supports selecting multiple coding languages when the role or interview requires them."
      },
      {
        "id": "4-4",
        "question": "Does Viriqo Hires evaluate code during the interview?",
        "answer": "Viriqo Hires' documented capabilities include real-time coding assessment and code correctness as part of technical candidate evaluation."
      },
      {
        "id": "4-5",
        "question": "Can a coding assessment be combined with an AI interview?",
        "answer": "Yes. Viriqo Hires is designed to bring AI-powered interviewing and technical assessment into one workflow rather than requiring teams to manage them as completely separate processes."
      },
      {
        "id": "4-6",
        "question": "Can coding assessment results contribute to candidate evaluation?",
        "answer": "Yes. Coding and technical assessment information can form part of the structured evaluation available to the hiring team after the interview."
      }
    ]
  },
  {
    "id": 5,
    "title": "Interview Monitoring & Proctoring",
    "description": "Questions about remote interview integrity, monitoring, and reviewable evidence.",
    "questions": [
      {
        "id": "5-1",
        "question": "Does Viriqo Hires support proctored remote technical interviews?",
        "answer": "Yes. Viriqo Hires includes remote interview monitoring and proctoring capabilities designed to give hiring teams greater visibility into the candidate's interview environment and activity."
      },
      {
        "id": "5-2",
        "question": "Can Viriqo Hires record the candidate's webcam?",
        "answer": "Yes. Webcam recording is part of Viriqo Hires' documented interview monitoring capabilities."
      },
      {
        "id": "5-3",
        "question": "Can Viriqo Hires record the candidate's screen?",
        "answer": "Yes. Viriqo Hires supports screen recording as part of the monitored interview experience."
      },
      {
        "id": "5-4",
        "question": "What is second-camera monitoring?",
        "answer": "Second-camera monitoring provides an additional camera view of the candidate's interview environment. It is designed to give reviewers more context than a single webcam view when additional monitoring is required."
      },
      {
        "id": "5-5",
        "question": "How does a candidate connect the second camera?",
        "answer": "Viriqo Hires supports a QR-based second-camera workflow that allows an additional device to be connected to the interview."
      },
      {
        "id": "5-6",
        "question": "Can Viriqo Hires detect unusual interview activity?",
        "answer": "Viriqo Hires' documented proctoring capabilities include identifying and logging certain candidate or environment anomalies so that the activity can be reviewed by the hiring team."
      },
      {
        "id": "5-7",
        "question": "What types of activity can Viriqo Hires flag?",
        "answer": "The documented product capabilities include monitoring for potential impersonation, phone-related activity, additional people, and other person or environment anomalies. Flagged activity is intended to provide reviewable context rather than act as a hiring decision on its own."
      },
      {
        "id": "5-8",
        "question": "Does a flagged event automatically mean that a candidate cheated?",
        "answer": "No. A proctoring flag indicates that an event or activity may require review. Hiring teams should examine the available recording, interview context, and supporting evidence before drawing conclusions."
      },
      {
        "id": "5-9",
        "question": "Can recruiters monitor an interview while it is happening?",
        "answer": "Yes. Viriqo Hires supports live monitoring of ongoing interviews."
      },
      {
        "id": "5-10",
        "question": "Are monitoring events available after the interview?",
        "answer": "Viriqo Hires' documented capabilities include logged violations or flagged activity together with reviewable interview evidence, allowing relevant events to be examined after the interview."
      }
    ]
  },
  {
    "id": 6,
    "title": "Candidate Evaluation & Scoring",
    "description": "How Viriqo Hires turns interview and assessment information into structured evaluation.",
    "questions": [
      {
        "id": "6-1",
        "question": "How does Viriqo Hires evaluate candidates?",
        "answer": "Viriqo Hires combines configured role requirements, interview performance, technical assessment information, and defined evaluation criteria into a structured candidate evaluation."
      },
      {
        "id": "6-2",
        "question": "Does Viriqo Hires provide an AI score?",
        "answer": "Yes. Completed interviews can include an AI-generated score as part of the evaluation information available to recruiters and hiring stakeholders."
      },
      {
        "id": "6-3",
        "question": "Can different evaluation criteria have different weightings?",
        "answer": "Yes. Teams can define evaluation weightings according to the requirements of the role so that different components contribute appropriately to the assessment."
      },
      {
        "id": "6-4",
        "question": "Can recruiters define benchmarks or pass/fail criteria?",
        "answer": "Viriqo Hires' documented product capabilities include weighted scoring, benchmarks, and pass/fail evaluation."
      },
      {
        "id": "6-5",
        "question": "Does Viriqo Hires make the final hiring decision?",
        "answer": "Viriqo Hires provides structured evaluation information and supporting interview evidence for hiring teams to review. Final employment decisions remain with the organization and its hiring stakeholders."
      },
      {
        "id": "6-6",
        "question": "Can hiring managers review the evidence behind an evaluation?",
        "answer": "Yes. Depending on what is captured for the interview, reviewers can use scores, reports, notes, recordings, transcripts, and flagged activity to understand the candidate's interview performance and context."
      },
      {
        "id": "6-7",
        "question": "Can recruiters add notes to candidate evaluations?",
        "answer": "Yes. Candidate notes are part of Viriqo Hires' documented evaluation and review capabilities."
      }
    ]
  },
  {
    "id": 7,
    "title": "Reports, Recordings & Interview Review",
    "description": "What hiring stakeholders can review after a technical interview is completed.",
    "questions": [
      {
        "id": "7-1",
        "question": "What information is available after an interview?",
        "answer": "Completed interview records can include an AI score, full interview report, video recordings, transcript, notes, and other evaluation or monitoring information captured during the interview."
      },
      {
        "id": "7-2",
        "question": "Does Viriqo Hires generate interview reports?",
        "answer": "Yes. Viriqo Hires supports full reports for completed interviews, giving hiring stakeholders a structured way to review interview outcomes."
      },
      {
        "id": "7-3",
        "question": "Can hiring managers review interview recordings?",
        "answer": "Yes. Recording playback is part of Viriqo Hires' documented platform capabilities."
      },
      {
        "id": "7-4",
        "question": "Are multiple camera recordings available for review?",
        "answer": "Where second-camera monitoring has been enabled and captured, Viriqo Hires can provide interview evidence from the available camera feeds."
      },
      {
        "id": "7-5",
        "question": "Does Viriqo Hires provide interview transcripts?",
        "answer": "Yes. Completed interviews can include a transcript for review."
      },
      {
        "id": "7-6",
        "question": "Can reports be downloaded?",
        "answer": "Yes. Viriqo Hires' documented reporting capabilities include report and PDF output."
      },
      {
        "id": "7-7",
        "question": "Can reports be shared with hiring stakeholders?",
        "answer": "Viriqo Hires provides reviewable reports and interview information that can support collaboration between recruiting and hiring stakeholders."
      }
    ]
  },
  {
    "id": 8,
    "title": "Interview Scheduling & Management",
    "description": "Questions about scheduling candidates and managing interviews across their lifecycle.",
    "questions": [
      {
        "id": "8-1",
        "question": "Can recruiters schedule interviews through Viriqo Hires?",
        "answer": "Yes. Recruiters can schedule candidate interviews against jobs or requisitions within the Viriqo Hires interview workflow."
      },
      {
        "id": "8-2",
        "question": "What candidate information is needed to schedule an interview?",
        "answer": "The current scheduling workflow includes candidate name, candidate email, resume, selected job or requisition, interview date, and interview time."
      },
      {
        "id": "8-3",
        "question": "Can second-camera monitoring be enabled when scheduling an interview?",
        "answer": "Yes. The scheduling workflow includes an option to enable a second camera feed for the interview."
      },
      {
        "id": "8-4",
        "question": "Can recruiters see interviews happening today?",
        "answer": "Yes. Viriqo Hires provides visibility into ongoing interviews and interviews that are pending for the current day."
      },
      {
        "id": "8-5",
        "question": "Can recruiters see future interviews?",
        "answer": "Yes. Upcoming scheduled interviews are available separately so recruiters can see interviews planned for future dates."
      },
      {
        "id": "8-6",
        "question": "Can recruiters review completed interviews separately?",
        "answer": "Yes. Completed interviews are separated from ongoing, pending, and upcoming interviews and include the available post-interview evaluation and review information."
      }
    ]
  },
  {
    "id": 9,
    "title": "Use Cases",
    "description": "How Viriqo Hires can support common technical hiring scenarios.",
    "questions": [
      {
        "id": "9-1",
        "question": "Can Viriqo Hires support high-volume technical hiring?",
        "answer": "Viriqo Hires' structured job setup, automated interviewing, assessment, scheduling, monitoring, and evaluation capabilities can help teams apply a more consistent technical interview process across larger candidate volumes."
      },
      {
        "id": "9-2",
        "question": "Can Viriqo Hires be used for developer hiring?",
        "answer": "Yes. Configurable technical skills, AI-powered interviewing, coding assessment, technical evaluation, and reviewable reports make developer hiring a core use case for Viriqo Hires."
      },
      {
        "id": "9-3",
        "question": "Can Viriqo Hires support remote hiring?",
        "answer": "Yes. Viriqo Hires supports remote technical interviewing together with screen and webcam recording, live monitoring, second-camera capabilities, and AI-powered proctoring."
      },
      {
        "id": "9-4",
        "question": "Can Viriqo Hires be used for technical screening?",
        "answer": "Yes. Teams can use configured skills, technical questions, coding assessment, and structured evaluation to assess candidates before they move further through the hiring process."
      },
      {
        "id": "9-5",
        "question": "Can Viriqo Hires support campus technical hiring?",
        "answer": "Viriqo Hires' structured technical interview and assessment workflow can be used by organizations that need to evaluate larger groups of candidates for technical roles, including campus hiring programs."
      },
      {
        "id": "9-6",
        "question": "Can different departments use Viriqo Hires for different technical roles?",
        "answer": "Yes. Because skills, questions, evaluation weightings, and coding requirements can be configured by job, different teams can adapt Viriqo Hires to the technical requirements of their individual roles."
      }
    ]
  },
  {
    "id": 10,
    "title": "Teams, Roles & User Management",
    "description": "How organizations can organize recruiting users and access within Viriqo Hires.",
    "questions": [
      {
        "id": "10-1",
        "question": "Can multiple recruiters use Viriqo Hires?",
        "answer": "Yes. Viriqo Hires supports multiple platform users and recruiter teams."
      },
      {
        "id": "10-2",
        "question": "Does Viriqo Hires support role-based access?",
        "answer": "Yes. Role-based access control is part of Viriqo Hires' documented platform capabilities."
      },
      {
        "id": "10-3",
        "question": "What user roles are supported?",
        "answer": "The current Viriqo Hires role hierarchy includes Super Admin, Super Manager, Manager, Lead, and Recruiter. The current user controls also include a Client Hiring Manager role."
      },
      {
        "id": "10-4",
        "question": "Can managers see activity from recruiters in their team?",
        "answer": "Viriqo Hires' role hierarchy provides different levels of visibility and control according to the user's role, including downward visibility for management roles."
      },
      {
        "id": "10-5",
        "question": "Can users be organized into teams?",
        "answer": "Yes. Viriqo Hires supports team-based organization, including recruiter teams that can be used in platform views and reporting."
      },
      {
        "id": "10-6",
        "question": "Can administrators invite users?",
        "answer": "Yes. Viriqo Hires' User Controls area includes an Invite User action for adding users to the platform."
      },
      {
        "id": "10-7",
        "question": "Can recruiter productivity be reviewed?",
        "answer": "Yes. Viriqo Hires includes recruiter productivity reporting with metrics such as requisitions created, interviews scheduled, and average interviews per requisition."
      }
    ]
  },
  {
    "id": 11,
    "title": "Candidate Experience",
    "description": "Questions buyers may have about what candidates experience during remote technical interviews.",
    "questions": [
      {
        "id": "11-1",
        "question": "What does a candidate need to participate in a Viriqo Hires interview?",
        "answer": "Candidates participate through the configured remote interview process and may need access to a compatible device, camera, screen-sharing or recording capability, and any additional monitoring setup required for that interview."
      },
      {
        "id": "11-2",
        "question": "Do candidates need a second camera for every interview?",
        "answer": "Viriqo Hires allows second-camera monitoring to be enabled as part of an interview configuration when the organization requires it."
      },
      {
        "id": "11-3",
        "question": "What happens before the interview begins?",
        "answer": "Viriqo Hires' documented capabilities include system checks intended to help validate the candidate's interview environment and technical setup."
      },
      {
        "id": "11-4",
        "question": "Can candidates complete coding tasks during the same interview?",
        "answer": "Yes. Coding assessment can be incorporated into the broader technical interview workflow."
      }
    ]
  },
  {
    "id": 12,
    "title": "Enterprise, Security, Privacy & AI Governance",
    "description": "Enterprise questions about access control, interview integrity, and the responsible use of AI outputs.",
    "questions": [
      {
        "id": "12-1",
        "question": "Does Viriqo Hires support role-based access control?",
        "answer": "Yes. Viriqo Hires includes role-based access controls for different platform users and levels of responsibility."
      },
      {
        "id": "12-2",
        "question": "How does Viriqo Hires help verify candidate identity and interview integrity?",
        "answer": "Viriqo Hires' documented capabilities include candidate identity checks, system checks, interview monitoring, webcam and screen recording, second-camera monitoring, anomaly detection, and reviewable interview evidence."
      },
      {
        "id": "12-3",
        "question": "How should organizations use AI-generated scores and proctoring flags?",
        "answer": "AI-generated evaluation information and proctoring flags should be treated as reviewable inputs within the organization's hiring process. They should not be presented as the sole basis for an employment decision."
      }
    ]
  },
  {
    "id": 13,
    "title": "Getting Started",
    "description": "Questions for organizations moving from product evaluation toward a demo or implementation discussion.",
    "questions": [
      {
        "id": "13-1",
        "question": "How can my organization evaluate Viriqo Hires?",
        "answer": "Organizations interested in Viriqo Hires can request a product demonstration to see how job setup, AI-powered interviewing, technical assessment, monitoring, evaluation, and reporting work together."
      },
      {
        "id": "13-2",
        "question": "What should we prepare before a Viriqo Hires demo?",
        "answer": "Bring an example technical role, the skills you need to evaluate, your current technical interview workflow, approximate hiring volume, and the challenges your recruiting or engineering teams are trying to solve. This helps the conversation focus on a realistic hiring scenario."
      },
      {
        "id": "13-3",
        "question": "How do I request a Viriqo Hires demo?",
        "answer": "Use the Book a Demo form and provide your organization and technical hiring requirements. The Viriqo Hires team can then follow up to discuss the most relevant product workflow."
      }
    ]
  }
];

